import { Theme, ThemeMode, ThemeName } from '../constants/theme'
import { getCurrentEvent } from './events'
import { EventType } from './events/types'

export const THEME_STORAGE_KEY = 'theme'
export const THEME_STORAGE_EVENT = 'theme:storage-update'
export const DEFAULT_THEME = Theme.GOLD_LIGHT
export type ThemeStorageEventDetail = {
  theme: Theme
}

// Parse
export function parseTheme(theme: string): Theme {
  // Legacy supported
  if (theme === 'light') {
    return Theme.GOLD_LIGHT
  } else if (theme === 'dark') {
    return Theme.GOLD_DARK
  }
  // Legacy supported ended

  if (!isTheme(theme)) {
    return DEFAULT_THEME
  }
  return theme
}

export function isTheme(theme: string): theme is Theme {
  if (typeof theme !== 'string') return false
  return Object.values(Theme).some((t) => t === theme)
}

// Helper functions for theme composition
export function getThemeName(theme: Theme): ThemeName {
  return theme.startsWith('gold') ? ThemeName.GOLD : ThemeName.BLUE
}

export function getThemeMode(theme: Theme): ThemeMode {
  return theme.endsWith('dark') ? ThemeMode.DARK : ThemeMode.LIGHT
}

export function composeTheme(name: ThemeName, mode: ThemeMode): Theme {
  return `${name}-${mode}` as Theme
}

export function getThemePath(theme: Theme): string {
  const name = getThemeName(theme)
  const mode = getThemeMode(theme)
  return `${name}/css/${mode}/styles.css`
}

// Storage
export function setThemeToStorage(theme: Theme) {
  if (typeof window !== 'undefined') {
    const existingTheme = getThemeFromStorage()
    if (existingTheme === theme) return

    localStorage.setItem(THEME_STORAGE_KEY, theme)
    window.dispatchEvent(
      new CustomEvent<ThemeStorageEventDetail>(THEME_STORAGE_EVENT, {
        detail: { theme },
      }),
    )
  }
}

export function clearThemeFromStorage() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(THEME_STORAGE_KEY)
  }
}

export function getThemeFromStorage(): Theme | null {
  if (typeof window !== 'undefined') {
    const theme = localStorage.getItem(THEME_STORAGE_KEY)
    return theme ? parseTheme(theme) : null
  }
  return null
}

export const updateDocumentBodyByTheme = (theme: Theme) => {
  const themeName = getThemeName(theme)
  const themeMode = getThemeMode(theme)

  // Check if an event is active
  const currentEvent = getCurrentEvent()

  let className = `theme-${themeName} theme-${themeMode}`

  // Add event-specific class
  if (currentEvent === EventType.CHRISTMAS) {
    className += ' event-christmas'
  } else if (currentEvent === EventType.HALLOWEEN) {
    className += ' event-halloween'
  } else if (currentEvent === EventType.AUTUMN_LEAVES) {
    className += ' event-autumn-leaves'
  } else if (currentEvent === EventType.SAKURA) {
    className += ' event-sakura'
  } else if (currentEvent === EventType.SONGKRAN) {
    className += ' event-songkran'
  } else if (currentEvent === EventType.CHINESE_NEW_YEAR) {
    className += ' event-chinese-new-year'
  } else if (currentEvent === EventType.LOY_KRATHONG) {
    className += ' event-loy-krathong'
  }

  document.body.className = className
}
